import { useQuery } from "react-query";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import "./register.scss";
import { BrandButton } from "../components/form/buttons";
import { BrandInput } from "../components/form/inputs";
import Loader from "../components/loader";
import LoadError from "../components/error";
import { ApiEndpoint } from "../components/utils";
import { z } from "zod";
import { Alert, Snackbar } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { pageIds } from "../components/utils";
import { getAppInfo, setAppInfo, setUserInfo } from "../components/user";

interface PageResponse {
  headerTitle?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  welcomeMessage?: any;
}

const Register = () => {
  const [data, setdata] = useState<PageResponse | null>();
  const [snackError, setSnackError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [checking, setChecking] = useState(false);
  const vertical = "bottom";
  const horizontal = "center";
  const notEmptyString = z.string().min(1);
  const validEmail = z.object({
    email: z
      .string()
      .min(1, { message: "Email field has to be filled." })
      .email("This is not a valid email."),
  });
  const email = useRef("");
  const firstName = useRef("");
  const lastName = useRef("");
  const password = useRef("");
  const parentEmail = useRef("");
  const navigate = useNavigate();
  const couponCode = useRef<string | null>(null);
  const [queryParameters] = useSearchParams();

  const { isLoading, error } = useQuery("registerPageData", async () => {
    const resp = await axios.get(
      ApiEndpoint + "wp-json/satapp-api/v1/getPageText?slug=" + pageIds.register
    );
    setdata(resp.data as PageResponse);
  });

  const doRegister = async () => {
    setChecking(true);
    const checkEmail = validEmail.safeParse({ email: email.current });
    const checkParentEmail = validEmail.safeParse({
      email: parentEmail.current,
    });
    const checkPassword = notEmptyString.safeParse(password.current);
    const checkFirstName = notEmptyString.safeParse(password.current);
    const checkLastName = notEmptyString.safeParse(password.current);

    if (
      checkEmail.success &&
      checkPassword.success &&
      checkFirstName.success &&
      checkLastName.success &&
      checkParentEmail.success
    ) {
      const resp = await axios.post(
        ApiEndpoint + "wp-json/satapp-api/v1/register",
        {
          email: email.current,
          password: password.current,
          first_name: firstName.current,
          last_name: lastName.current,
          parent_guardian_email: parentEmail.current,
          userType: 0,
          couponCode: couponCode.current ? couponCode.current : null,
        }
      );
      setChecking(false);
      if (resp.data) {
        setUserInfo(resp.data, true);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const success: any = resp.data.success;

        if (success) {
          navigate("/register-confirm", { replace: true });
        } else {
          setErrorMsg(resp.data.message);
          setSnackError(true);
        }
      } else {
        setErrorMsg("Error Registering. Please Try Again.");
        setSnackError(true);
      }
    } else {
      setErrorMsg("Please fill in all appropriate fields before registering");
      setSnackError(true);
      setChecking(false);
    }
  };

  const closeSnack = () => {
    setSnackError(false);
  };

  useEffect(() => {
    const passedCode = queryParameters.get("couponID");
    if (passedCode) {
      setAppInfo({ couponCode: passedCode });
      setUserInfo({ couponCode:passedCode });
      couponCode.current = passedCode;
    } else {
      const code = getAppInfo()?.couponCode;
      couponCode.current = code ? code : null;
    }
  }, []);

  if (isLoading) return <Loader />;
  if (error) return <LoadError />;

  return (
    <div className="registerPage">
      <div className="headline">
        <h1>{data?.headerTitle}</h1>
        <div
          className="description"
          dangerouslySetInnerHTML={{ __html: data?.welcomeMessage }}
        />
      </div>
      <div className="loginForm">
        <div className="formRow">
          <BrandInput
            autoComplete="no"
            onChange={(evt) => (parentEmail.current = evt.target.value)}
            id="filled-basic"
            label="Parent/Guardian Email"
            variant="filled"
          />
          <BrandInput
            autoComplete="no"
            onChange={(evt) => (firstName.current = evt.target.value)}
            id="filled-basic"
            label="First Name"
            variant="filled"
          />
          <BrandInput
            autoComplete="no"
            onChange={(evt) => (lastName.current = evt.target.value)}
            id="filled-basic"
            label="Last Name"
            variant="filled"
          />
        </div>
        <div className="formRow">
          <BrandInput
            autoComplete="no"
            onChange={(evt) => (email.current = evt.target.value)}
            id="filled-basic"
            label="Email"
            variant="filled"
          />
          <BrandInput
            id="filled-basic"
            label="Password"
            type="password"
            variant="filled"
            autoComplete="no"
            onChange={(evt) => (password.current = evt.target.value)}
          />
        </div>
        <div className="formRow">
          {checking ? (
            <FontAwesomeIcon icon={faSpinner} />
          ) : (
            <BrandButton onClick={doRegister}>REGISTER</BrandButton>
          )}
        </div>
      </div>
      <Snackbar
        open={snackError}
        autoHideDuration={6000}
        onClose={closeSnack}
        anchorOrigin={{ vertical, horizontal }}
      >
        <Alert severity="error">{errorMsg}</Alert>
      </Snackbar>
    </div>
  );
};

export default Register;
